import {
  Group,
  Text,
  Stack,
  Badge,
  Tooltip,
  ActionIcon,
  Menu,
} from '@mantine/core';
import {
  Download,
  ExternalLink,
  MoreVertical,
  Edit,
  Trash2,
  EyeOff,
  File,
  Link as LinkIcon,
  FileText,
  Image,
  Film,
} from 'react-feather';
import type { ResourceWithDetails } from '../../../types';
import type { ColumnConfig } from '../../../hooks';
import { formatDate } from '../../../utils/formatters';
import { allIdsSelected, groupLabel, removeSessionIds } from '../../../components/common/SessionMultiSelect/sessionSelection';

/** Map MIME types to icons */
function getResourceIcon(resource: ResourceWithDetails) {
  if (resource.resource_type === 'link') {
    return <LinkIcon size={16} />;
  }
  const mimeType = resource.mime_type || '';
  if (mimeType.startsWith('image/')) return <Image size={16} />;
  if (mimeType.startsWith('video/')) return <Film size={16} />;
  if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('word')) {
    return <FileText size={16} />;
  }
  return <File size={16} />;
}

/** Format file size for display */
function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Render the title cell with description, evergreen badge, and file info */
function renderTitleCell(resource: ResourceWithDetails) {
  return (
    <Stack gap={2}>
      <Group gap="xs">
        <Text fw={500} size="sm">
          {resource.title}
        </Text>
        {resource.is_evergreen && (
          <Badge size="xs" color="teal" variant="light">
            EVERGREEN
          </Badge>
        )}
      </Group>
      {resource.description && (
        <Text size="xs" c="dimmed" lineClamp={1}>
          {resource.description}
        </Text>
      )}
      {resource.resource_type === 'file' && resource.file_size && (
        <Text size="xs" c="dimmed">
          {resource.original_filename} ({formatFileSize(resource.file_size)})
        </Text>
      )}
    </Stack>
  );
}

/** Render the sessions cell */
function renderSessionsCell(resource: ResourceWithDetails, linkableSessionIds: string[]) {
  if (!resource.sessions || resource.sessions.length === 0) {
    return <Text c="dimmed" size="sm">General</Text>;
  }
  // MED-1400: match the picker, which says "All sessions (N)" when every session is linked.
  const linkedIds = resource.sessions.map((s) => s.session_id);
  // MED-1400: only sessions in the picker list get a session label. Every other link (for
  // example a 1:1 session, which also has no group) counts as "other", as in the picker.
  const otherCount = removeSessionIds(linkedIds, linkableSessionIds).length;
  if (linkableSessionIds.length > 1 && allIdsSelected(linkedIds, linkableSessionIds)) {
    return (
      <Stack gap={2}>
        <Text size="sm">All sessions ({linkableSessionIds.length})</Text>
        {otherCount > 0 && (
          <Text size="xs" c="dimmed">
            +{otherCount} other
          </Text>
        )}
      </Stack>
    );
  }
  const listed = new Set(linkableSessionIds);
  const shown = resource.sessions.filter((s) => listed.has(s.session_id));
  if (shown.length === 0) {
    return (
      <Text size="sm">
        {otherCount} other linked session{otherCount === 1 ? '' : 's'}
      </Text>
    );
  }
  return (
    <Stack gap={2}>
      {shown.slice(0, 2).map((s) => (
        <Text key={s.session_id} size="sm">
          {s.session_type === 'workshop_session'
            ? (s.title || `Workshop ${s.session_number}`)
            : s.is_all_groups
              ? `All Groups - Session ${s.session_number}`
              : `${s.group_name ? `${groupLabel(s.group_name)} - ` : ''}Session ${s.session_number}`}
        </Text>
      ))}
      {shown.length > 2 && (
        <Text size="xs" c="dimmed">
          +{shown.length - 2} more
        </Text>
      )}
      {otherCount > 0 && (
        <Text size="xs" c="dimmed">
          +{otherCount} other
        </Text>
      )}
    </Stack>
  );
}

/** Render access badges */
function renderAccessBadges(resource: ResourceWithDetails) {
  return (
    <Group gap={4}>
      {resource.access_coaches && (
        <Badge size="xs" color="blue" variant="light">COACHES</Badge>
      )}
      {resource.access_participants && (
        <Badge size="xs" color="green" variant="light">PARTICIPANTS</Badge>
      )}
      {resource.access_partners && (
        <Badge size="xs" color="purple" variant="light">PARTNERS</Badge>
      )}
    </Group>
  );
}

/** Main cell content renderer for resources table */
export function renderResourceCellContent(
  column: ColumnConfig,
  resource: ResourceWithDetails,
  linkableSessionIds: string[] = [],
) {
  switch (column.id) {
    case 'icon':
      return <Group gap={4}>{getResourceIcon(resource)}</Group>;
    case 'title':
      return renderTitleCell(resource);
    case 'category':
      return resource.category_name ? (
        <Badge variant="light" color="gray" size="sm">{resource.category_name}</Badge>
      ) : (
        <Text c="dimmed" size="sm">&mdash;</Text>
      );
    case 'sessions':
      return renderSessionsCell(resource, linkableSessionIds);
    case 'access':
      return renderAccessBadges(resource);
    case 'added':
      return <Text size="sm">{formatDate(resource.created_at)}</Text>;
    default:
      return <Text size="sm">&mdash;</Text>;
  }
}

/** Render the actions column for a resource row */
export function renderResourceActions(
  resource: ResourceWithDetails,
  handlers: {
    onOpen: (resource: ResourceWithDetails) => void;
    onEdit: (resource: ResourceWithDetails) => void;
    onDelete: (resource: ResourceWithDetails) => void;
    onHideEvergreen: (resource: ResourceWithDetails) => void;
  },
) {
  return (
    <Group gap={4} wrap="nowrap">
      <Tooltip label={resource.resource_type === 'link' ? 'Open link' : 'Download'}>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => handlers.onOpen(resource)}
        >
          {resource.resource_type === 'link' ? (
            <ExternalLink size={16} />
          ) : (
            <Download size={16} />
          )}
        </ActionIcon>
      </Tooltip>
      <Menu shadow="md" width={160} position="bottom-end">
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray">
            <MoreVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {!resource.is_evergreen && (
            <>
              <Menu.Item leftSection={<Edit size={14} />} onClick={() => handlers.onEdit(resource)}>
                Edit
              </Menu.Item>
              <Menu.Item leftSection={<Trash2 size={14} />} color="red" onClick={() => handlers.onDelete(resource)}>
                Delete
              </Menu.Item>
            </>
          )}
          {resource.is_evergreen && (
            <Menu.Item leftSection={<EyeOff size={14} />} onClick={() => handlers.onHideEvergreen(resource)}>
              Hide from cohort
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
